import React, { useState } from 'react';

export default function ITConsulting() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    consultingArea: 'Strategic IT Roadmap',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans selection:bg-orange-500 selection:text-white">

      {/* ==========================================
          SECTION 1: HERO / STRATEGIC ADVISORY
         ========================================== */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28 border-b border-orange-100">
        {/* Decorative Background Gradients */}
        <div className="absolute top-1/3 left-1/4 -z-10 w-96 h-96 bg-orange-400/15 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute -top-20 -right-20 -z-10 w-96 h-96 bg-amber-300/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#f973160a_1px,transparent_1px),linear-gradient(to_bottom,#f973160a_1px,transparent_1px)] bg-[size:28px_28px]" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 font-bold text-xs tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              Strategic Advisory & Growth
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 tracking-tight leading-[1.1]">
              IT Consulting Built to <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500">
                Drive Business Growth
              </span>
            </h1>

            <p className="text-lg text-stone-600 max-w-xl leading-relaxed">
              Expert guidance to optimize your IT strategy, modernize legacy architectures, and align technology investments with your overarching business goals.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#consulting-form"
                className="px-8 py-4 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-bold transition-all duration-200 shadow-xl shadow-orange-500/25 hover:shadow-orange-500/40 transform hover:-translate-y-0.5"
              >
                Schedule Strategy Session
              </a>
              <a
                href="#services-section"
                className="px-8 py-4 rounded-xl bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-800 font-semibold transition-all duration-200"
              >
                Explore Services
              </a>
            </div>

            {/* Impact Metrics */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-orange-100">
              <div>
                <p className="text-3xl font-extrabold text-stone-900">35%+</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">ROI Improvement</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-orange-600">100+</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Projects Delivered</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-stone-900">10x</p>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-1">Faster Deployment</p>
              </div>
            </div>
          </div>

          {/* Right Visual Dashboard Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative bg-stone-900 text-white border border-stone-800 rounded-3xl p-6 shadow-2xl">
              <div className="flex items-center justify-between pb-4 border-b border-stone-800">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs font-mono text-stone-300">Strategy Matrix: Optimal</span>
                </div>
                <span className="text-[11px] font-mono bg-orange-500/10 text-orange-400 border border-orange-500/30 px-2.5 py-0.5 rounded-full">
                  Enterprise Ready
                </span>
              </div>

              {/* Simulated Roadmap Progress */}
              <div className="py-6 space-y-5">
                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Legacy Modernization</span>
                    <span className="text-orange-400 font-bold">88% Completed</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[88%]" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Tech Cost Optimization</span>
                    <span className="text-orange-400 font-bold">42% Reduced</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[65%]" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Digital Transformation Index</span>
                    <span className="text-orange-400 font-bold">High Alignment</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[94%]" />
                  </div>
                </div>
              </div>

              <div className="p-3.5 bg-stone-950 rounded-xl border border-stone-800 flex items-center justify-between text-xs font-mono">
                <span className="text-stone-400">Consulting Advisory Standard</span>
                <span className="text-emerald-400 font-semibold flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  ISO 27001 Aligned
                </span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ==========================================
          SECTION 2: CONSULTING SERVICES
         ========================================== */}
      <section id="services-section" className="py-24 bg-orange-50/40">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-orange-600 font-bold text-xs uppercase tracking-widest bg-orange-100 border border-orange-200 px-3 py-1 rounded-full">
              Tailored Expertise
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Comprehensive IT Consulting Services
            </h2>
            <p className="text-stone-600 text-base">
              End-to-end technology advisory built to streamline workflows, eliminate tech debt, and fuel measurable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Strategic IT Roadmap
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Design actionable technology roadmaps aligned with long-term business objectives and revenue milestones.
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Digital Transformation
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Modernize legacy systems, introduce automation, and build resilient digital-first operating models.
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Infrastructure Advisory
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Evaluate, design, and optimize cloud and hybrid infrastructure to maximize performance and minimize downtime.
              </p>
            </div>

            {/* Service 4 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                IT Cost & Budget Optimization
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Audit software licensing, cloud expenditures, and vendor contracts to eliminate waste and optimize budget allocation.
              </p>
            </div>

            {/* Service 5 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Software & Stack Selection
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Unbiased evaluation and procurement support to help you select tools and tech stacks tailored to your team.
              </p>
            </div>

            {/* Service 6 */}
            <div className="bg-white p-8 rounded-2xl border border-orange-100 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Governance & Risk Management
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Establish robust IT governance frameworks, compliance standards, and disaster recovery strategies.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ==========================================
          SECTION 3: CONSULTING METHODOLOGY & FORM
         ========================================== */}
      <section id="consulting-form" className="py-24 bg-white border-t border-orange-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-950 text-white rounded-3xl p-8 lg:p-14 shadow-2xl relative overflow-hidden">
            
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 blur-3xl pointer-events-none" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
              
              {/* Left Column: Methodology */}
              <div className="lg:col-span-6 space-y-6">
                <span className="bg-orange-500/10 text-orange-400 border border-orange-500/30 font-semibold text-xs px-3.5 py-1.5 rounded-full uppercase tracking-wider">
                  Our Methodology
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                  A Proven Framework for Sustainable Success
                </h2>
                <p className="text-stone-400 text-base leading-relaxed">
                  We bridge the gap between technical execution and business vision through a structured, transparent consulting model.
                </p>

                {/* Steps */}
                <div className="space-y-6 pt-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      01
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Discovery & Audit</h4>
                      <p className="text-sm text-stone-400 mt-0.5">In-depth assessment of your current infrastructure, workflows, and pain points.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      02
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Strategic Planning</h4>
                      <p className="text-sm text-stone-400 mt-0.5">Custom roadmap design with prioritize action items, timelines, and ROI metrics.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      03
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Execution & Support</h4>
                      <p className="text-sm text-stone-400 mt-0.5">Hands-on guidance during implementation with ongoing review cycles.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Form */}
              <div className="lg:col-span-6">
                <div className="bg-white text-stone-900 border border-orange-100 rounded-2xl p-6 sm:p-8 shadow-2xl">
                  <h3 className="text-2xl font-bold text-stone-900 mb-1">
                    Book a Free Strategy Session
                  </h3>
                  <p className="text-stone-600 text-sm mb-6">
                    Connect with a senior IT consultant to discuss your technology roadmap.
                  </p>

                  {submitted ? (
                    <div className="bg-orange-50 border border-orange-200 text-stone-800 p-8 rounded-xl text-center space-y-3">
                      <div className="w-14 h-14 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto text-2xl font-bold shadow-lg shadow-orange-600/30">
                        ✓
                      </div>
                      <h4 className="font-bold text-xl text-stone-900">Session Requested!</h4>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        A senior advisor will review your details and get back to you within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Morgan Vance"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Work Email
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="morgan@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Company Name
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Acme Corp"
                          value={formData.companyName}
                          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Consulting Focus
                        </label>
                        <select
                          value={formData.consultingArea}
                          onChange={(e) => setFormData({ ...formData, consultingArea: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        >
                          <option value="Strategic IT Roadmap">Strategic IT Roadmap</option>
                          <option value="Digital Transformation">Digital Transformation</option>
                          <option value="Infrastructure Advisory">Infrastructure Advisory</option>
                          <option value="IT Cost & Budget Optimization">IT Cost & Budget Optimization</option>
                          <option value="Software & Stack Selection">Software & Stack Selection</option>
                          <option value="Governance & Risk Management">Governance & Risk Management</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Briefly Describe Your Business Goals
                        </label>
                        <textarea
                          rows="3"
                          required
                          placeholder="Tell us about your current technology challenges or goals..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-600/30 hover:shadow-orange-600/40 mt-2"
                      >
                        Request Strategy Session
                      </button>
                    </form>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}